import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PinyinMatch from "pinyin-match";
import {
  BIBLE_CATEGORIES_ZH,
  BIBLE_CATEGORIES_EN,
  getBookAbbreviation,
  getBookCategory,
} from "../../bible/bibleCategories";
import { ZH_BOOK_ALIASES } from "../../bible/zhBookAliases";
import { getReadChapters } from "../ChapterProgress/useChapterProgress";

const cleanBookName = (raw = "") =>
  raw.includes("-") ? raw.split("-").slice(1).join("-") : raw;

const parseBookIndex = (chapter) => {
  const idPart = parseInt(String(chapter?.id || "").split("-")[0], 10);
  if (Number.isFinite(idPart) && idPart > 0) return idPart;
  const bookPart = parseInt(String(chapter?.bookName || "").split("-")[0], 10);
  return Number.isFinite(bookPart) && bookPart > 0 ? bookPart : 1;
};

export const buildCatalogBooks = (allChapters = [], isEn = false) => {
  const map = new Map();
  allChapters.forEach((ch) => {
    const rawBook = ch.bookName || "";
    const name = cleanBookName(rawBook) || ch.title?.replace(/^#\s*/, "") || "";
    if (!map.has(name)) {
      const bookIndex = parseBookIndex(ch);
      const testament = bookIndex <= 39 ? "ot" : "nt";
      const category = getBookCategory(name, isEn);
      map.set(name, {
        name,
        rawBook,
        bookIndex,
        testament,
        categoryId: category?.id || "all",
        categoryLabel: category?.shortLabel || category?.label || "",
        abbr: getBookAbbreviation(name),
        chapters: [],
      });
    }
    map.get(name).chapters.push(ch);
  });
  return Array.from(map.values());
};

export const matchCatalogBook = (book, query, usesPinyin = true) => {
  const q = String(query || "").trim();
  if (!q) return true;
  const lower = q.toLowerCase();
  const nameLower = book.name.toLowerCase();
  const abbrLower = (book.abbr || "").toLowerCase();

  if (nameLower.includes(lower) || abbrLower.includes(lower)) {
    return true;
  }

  const aliasMatches = ZH_BOOK_ALIASES[q];
  if (aliasMatches && aliasMatches.includes(book.name)) {
    return true;
  }

  if (lower === "cyj" && book.name === "创世记") {
    return true;
  }

  if (usesPinyin) {
    try {
      if (PinyinMatch.match(book.name, q)) {
        return true;
      }
    } catch (e) {
      // ignore pinyin match error
    }
  }

  return false;
};

const CatalogDrawer = ({
  open,
  onClose,
  allChapters = [],
  currentChapterId,
  chapterTitles = {},
  onSelectChapter,
  edition,
  isMobile = false,
}) => {
  const isEn = edition?.id === "en";
  const usesPinyin = Boolean(edition?.usesPinyin);
  const categoriesConfig = isEn ? BIBLE_CATEGORIES_EN : BIBLE_CATEGORIES_ZH;

  const books = useMemo(
    () => buildCatalogBooks(allChapters, isEn),
    [allChapters, isEn]
  );

  const currentBook = useMemo(() => {
    if (!currentChapterId) return books[0] || null;
    return (
      books.find((b) => b.chapters.some((ch) => ch.id === currentChapterId)) ||
      books[0] ||
      null
    );
  }, [books, currentChapterId]);

  const [testament, setTestament] = useState("ot");
  const [categoryId, setCategoryId] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedBooks, setExpandedBooks] = useState({});
  const [readMap, setReadMap] = useState({});
  const listContainerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setReadMap(getReadChapters());
    if (currentBook) {
      setTestament(currentBook.testament);
      setCategoryId("all");
      setExpandedBooks((prev) => ({
        ...prev,
        [currentBook.name]: true,
      }));
    }
    const timer = window.setTimeout(() => {
      if (!currentBook || !listContainerRef.current) return;
      const node = listContainerRef.current.querySelector(
        `[data-catalog-book="${currentBook.name}"]`
      );
      if (node && typeof node.scrollIntoView === "function") {
        node.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    }, 60);
    return () => window.clearTimeout(timer);
  }, [currentBook, open]);

  const activeCategories = useMemo(() => {
    return testament === "nt" ? categoriesConfig.nt : categoriesConfig.ot;
  }, [categoriesConfig, testament]);

  const filteredBooks = useMemo(() => {
    const q = searchQuery.trim();
    if (q) {
      return books.filter((book) => matchCatalogBook(book, q, usesPinyin));
    }
    return books.filter((book) => {
      if (book.testament !== testament) return false;
      if (categoryId && categoryId !== "all") {
        return book.categoryId === categoryId;
      }
      return true;
    });
  }, [books, categoryId, searchQuery, testament, usesPinyin]);

  const toggleBook = (bookName) => {
    setExpandedBooks((prev) => ({
      ...prev,
      [bookName]: !prev[bookName],
    }));
  };

  const handleSelect = (chapterId) => {
    if (onSelectChapter) {
      onSelectChapter(chapterId);
    }
    if (onClose) {
      onClose();
    }
  };

  const isSearching = Boolean(searchQuery.trim());

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : 420,
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          pb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <MenuBookIcon color="primary" fontSize="small" />
          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: "1.05rem" }}>
            {edition?.ui?.catalogTitle || "圣经目录"}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label={edition?.ui?.close || "close"}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Search Input */}
      <Box sx={{ px: 2, pb: 1.5 }}>
        <TextField
          id="catalog-book-search"
          name="catalogSearch"
          fullWidth
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={
            edition?.ui?.catalogSearchPlaceholder ||
            "搜索书卷、简写或拼音（如：创、cyj）"
          }
          inputProps={{
            name: "catalogSearch",
            "aria-label":
              edition?.ui?.catalogSearchPlaceholder || "搜索书卷",
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchQuery("")}
                  aria-label={edition?.ui?.clearSearch || "清空搜索"}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      {/* Testament Tabs + Category Chips */}
      {!isSearching && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Tabs
            value={testament}
            onChange={(e, val) => {
              setTestament(val);
              setCategoryId("all");
            }}
            variant="fullWidth"
            sx={{
              minHeight: 36,
              mb: 1,
              "& .MuiTab-root": {
                minHeight: 36,
                py: 0.5,
                fontWeight: 700,
                fontSize: "0.85rem",
                textTransform: "none",
              },
            }}
          >
            <Tab
              value="ot"
              label={edition?.ui?.oldTestament || "旧约 (39卷)"}
            />
            <Tab
              value="nt"
              label={edition?.ui?.newTestament || "新约 (27卷)"}
            />
          </Tabs>

          <Box
            sx={{
              display: "flex",
              gap: 0.75,
              overflowX: "auto",
              pb: 0.5,
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {activeCategories.map((cat) => {
              const selected = categoryId === cat.id;
              return (
                <Chip
                  key={cat.id}
                  label={cat.shortLabel || cat.label}
                  size="small"
                  clickable
                  color={selected ? "primary" : "default"}
                  variant={selected ? "filled" : "outlined"}
                  onClick={() => setCategoryId(cat.id)}
                  sx={{
                    fontWeight: selected ? 700 : 500,
                    fontSize: "0.75rem",
                    flexShrink: 0,
                  }}
                />
              );
            })}
          </Box>
        </Box>
      )}

      <Divider />

      {/* Book & Chapter Accordion List */}
      <Box
        ref={listContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 1.5,
        }}
      >
        {filteredBooks.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {edition?.ui?.catalogNoMatch || "未找到匹配的书卷"}
            </Typography>
          </Box>
        ) : (
          filteredBooks.map((book) => {
            const isCurrentBook = currentBook?.name === book.name;
            const isExpanded = Boolean(
              expandedBooks[book.name] ||
                (isSearching && filteredBooks.length <= 5)
            );
            const totalCount = book.chapters.length;
            const readCount = book.chapters.reduce(
              (acc, ch) => acc + (readMap[ch.id] ? 1 : 0),
              0
            );

            return (
              <Box
                key={book.name}
                data-catalog-book={book.name}
                sx={{
                  mb: 1,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: isCurrentBook ? "primary.main" : "divider",
                  bgcolor: isCurrentBook
                    ? (t) =>
                        t.palette.mode === "dark"
                          ? "rgba(226, 196, 138, 0.06)"
                          : "rgba(196, 162, 101, 0.06)"
                    : "background.paper",
                  overflow: "hidden",
                }}
              >
                <ButtonBase
                  onClick={() => toggleBook(book.name)}
                  sx={{
                    width: "100%",
                    px: 1.5,
                    py: 1.1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    textAlign: "left",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: 0,
                    }}
                  >
                    <Chip
                      label={book.abbr}
                      size="small"
                      color={isCurrentBook ? "primary" : "default"}
                      sx={{
                        height: 22,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        minWidth: 32,
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: isCurrentBook ? 800 : 600,
                        fontSize: "0.92rem",
                        color: "text.primary",
                        noWrap: true,
                      }}
                    >
                      {book.name}
                    </Typography>
                    {book.categoryLabel && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontSize: "0.72rem",
                        }}
                      >
                        · {book.categoryLabel}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: readCount > 0 ? "primary.main" : "text.secondary",
                        fontWeight: readCount > 0 ? 700 : 500,
                        fontSize: "0.75rem",
                      }}
                    >
                      {readCount > 0
                        ? `${readCount}/${totalCount}`
                        : edition?.ui?.chapterCountLabel
                          ? edition.ui.chapterCountLabel(totalCount)
                          : `${totalCount} 章`}
                    </Typography>
                    {isExpanded ? (
                      <ExpandLessIcon fontSize="small" color="action" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" color="action" />
                    )}
                  </Box>
                </ButtonBase>

                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Divider />
                  <Box
                    sx={{
                      p: 1.25,
                      display: "grid",
                      gridTemplateColumns: "repeat(5, 1fr)",
                      gap: 0.75,
                    }}
                  >
                    {book.chapters.map((ch) => {
                      const isCurrent = ch.id === currentChapterId;
                      const isRead = Boolean(readMap[ch.id]);
                      const subtitle = chapterTitles[ch.id] || "";
                      const btn = (
                        <Button
                          key={ch.id}
                          size="small"
                          variant={isCurrent ? "contained" : "outlined"}
                          color={isCurrent ? "primary" : "inherit"}
                          onClick={() => handleSelect(ch.id)}
                          aria-current={isCurrent ? "page" : undefined}
                          sx={{
                            minWidth: 0,
                            py: 0.6,
                            px: 0.5,
                            fontWeight: isCurrent ? 800 : 600,
                            fontSize: "0.8rem",
                            borderRadius: 1.5,
                            position: "relative",
                            borderColor: isCurrent
                              ? "primary.main"
                              : isRead
                                ? "primary.light"
                                : "divider",
                            bgcolor:
                              !isCurrent && isRead
                                ? (t) =>
                                    t.palette.mode === "dark"
                                      ? "rgba(226, 196, 138, 0.1)"
                                      : "rgba(196, 162, 101, 0.12)"
                                : undefined,
                          }}
                        >
                          {ch.chapterNumber}
                          {isRead && !isCurrent && (
                            <CheckCircleIcon
                              sx={{
                                fontSize: 11,
                                color: "primary.main",
                                position: "absolute",
                                top: 2,
                                right: 2,
                              }}
                            />
                          )}
                        </Button>
                      );

                      return subtitle ? (
                        <Tooltip key={ch.id} title={subtitle} placement="top" arrow>
                          <span>{btn}</span>
                        </Tooltip>
                      ) : (
                        btn
                      );
                    })}
                  </Box>
                </Collapse>
              </Box>
            );
          })
        )}
      </Box>
    </Drawer>
  );
};

export default CatalogDrawer;
